import { useEffect, useRef, useState } from "react";
import { X, MessageSquare, Paperclip, Send, FileText, Trash2, Clock } from "lucide-react";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import toast from "react-hot-toast";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface CommentAttachment {
  url: string;
  key: string;
  name: string;
}

export interface MilestoneComment {
  _id: string;
  text: string;
  attachments: CommentAttachment[];
  createdAt: string; // ISO string
  author?: {
    name: string;
  };
}

export interface MilestoneCommentDrawerProps {
  open: boolean;
  onClose: () => void;
  milestoneName: string;
  milestoneId: string;
  fetchComments: (milestoneId: string) => Promise<MilestoneComment[]>;
  saveComment: (milestoneId: string, text: string, attachments: CommentAttachment[]) => Promise<MilestoneComment>;
  deleteComment?: (milestoneId: string, commentId: string) => Promise<void>;
  uploadFile?: (file: File, onProgress: (pct: number) => void) => Promise<CommentAttachment>;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatTs(iso: string) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
}

function fileNameFromUrl(url: string) {
  try { return decodeURIComponent(new URL(url).pathname.split("/").pop() || url); }
  catch { return url; }
}

// ── Styles ────────────────────────────────────────────────────────────────────

const d: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed", inset: 0, zIndex: 9998,
    background: "rgba(0,0,0,0.18)", backdropFilter: "blur(1px)",
  },
  drawer: {
    position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 9999,
    width: "420px", maxWidth: "95vw",
    background: "#fff", boxShadow: "-6px 0 32px rgba(0,0,0,0.12)",
    display: "flex", flexDirection: "column",
    transition: "transform 0.28s cubic-bezier(.4,0,.2,1)",
    borderRadius: "16px 0 0 16px",
    overflow: "hidden",
  },
  drawerHidden: { transform: "translateX(100%)" },
  drawerVisible: { transform: "translateX(0)" },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "18px 20px 14px",
    borderBottom: "1px solid #f0f0f0",
    background: "#fafafa", flexShrink: 0,
  },
  headerLeft: { display: "flex", alignItems: "center", gap: "10px" },
  headerIcon: {
    width: "34px", height: "34px", borderRadius: "9px",
    background: "#1a1a1a", display: "flex", alignItems: "center", justifyContent: "center",
  },
  headerTitle: { fontSize: "13.5px", fontWeight: 700, color: "#1a1a1a", marginBottom: "1px" },
  headerSub: { fontSize: "11px", color: "#9ca3af", display: "flex", alignItems: "center", gap: "4px" },
  closeBtn: {
    width: "30px", height: "30px", borderRadius: "8px", border: "none",
    background: "#f3f4f6", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    color: "#6b7280", transition: "background 0.15s",
  },
  commentList: {
    flex: 1, overflowY: "auto" as const, padding: "14px 20px",
    display: "flex", flexDirection: "column", gap: "12px",
  },
  emptyState: {
    flex: 1, display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center", gap: "8px",
    color: "#9ca3af",
  },
  emptyIcon: {
    width: "44px", height: "44px", borderRadius: "50%",
    background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center",
  },
  emptyText: { fontSize: "13px", fontWeight: 500, color: "#6b7280" },
  emptyHint: { fontSize: "11.5px", color: "#9ca3af" },
  commentCard: {
    background: "#fafafa", border: "1px solid #efefef",
    borderRadius: "10px", padding: "11px 13px", position: "relative" as const,
  },
  commentMeta: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    marginBottom: "6px",
  },
  commentTs: {
    display: "flex", alignItems: "center", gap: "5px",
    fontSize: "10.5px", color: "#9ca3af",
  },
  commentAuthor: {
    fontSize: "11px", fontWeight: 600, color: "#374151",
    background: "#f3f4f6", padding: "2px 8px", borderRadius: "99px",
  },
  commentText: { fontSize: "13px", color: "#374151", lineHeight: 1.55 },
  commentAttachments: { marginTop: "8px", display: "flex", flexDirection: "column" as const, gap: "5px" },
  commentFileRow: { display: "flex", alignItems: "center", gap: "5px" },
  commentFileLink: {
    fontSize: "11.5px", color: "#2563eb", textDecoration: "none",
    maxWidth: "280px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const,
  },
  commentDeleteBtn: {
    position: "absolute" as const, top: "8px", right: "8px",
    background: "none", border: "none", cursor: "pointer",
    color: "#d1d5db", padding: "3px", borderRadius: "5px",
    display: "flex", alignItems: "center",
    transition: "color 0.15s, background 0.15s",
  },
  deletingOverlay: {
    position: "absolute" as const, inset: 0,
    background: "rgba(255,255,255,0.75)", borderRadius: "10px",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "11px", color: "#9ca3af", pointerEvents: "none" as const,
  },
  compose: {
    flexShrink: 0, padding: "14px 20px",
    borderTop: "1px solid #f0f0f0", background: "#fff",
  },
  textArea: {
    width: "100%", resize: "none" as const,
    border: "1px solid #e2e2e2", borderRadius: "9px",
    padding: "10px 12px", fontSize: "13px", fontFamily: "inherit",
    color: "#374151", outline: "none", lineHeight: 1.5,
    transition: "border-color 0.15s, box-shadow 0.15s",
    boxSizing: "border-box" as const,
    background: "#fafafa",
  },
  composeFooter: {
    display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "8px",
  },
  attachRow: { display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" as const },
  attachBtn: {
    display: "inline-flex", alignItems: "center", gap: "4px",
    background: "#f3f4f6", border: "1px solid #e2e2e2", borderRadius: "6px",
    padding: "5px 10px", fontSize: "11px", fontWeight: 600,
    color: "#6b7280", cursor: "pointer", fontFamily: "inherit",
  },
  pendingFile: {
    display: "inline-flex", alignItems: "center", gap: "4px",
    fontSize: "11px", color: "#374151",
    background: "#fef9c3", border: "1px solid #fde68a",
    borderRadius: "5px", padding: "3px 8px",
  },
  sendBtn: {
    display: "inline-flex", alignItems: "center", gap: "5px",
    background: "#F8B71F", border: "none", borderRadius: "7px",
    padding: "7px 14px", fontSize: "12.5px", fontWeight: 700,
    color: "#1a1a1a", cursor: "pointer", fontFamily: "inherit",
    boxShadow: "0 1px 4px rgba(248,183,31,0.35)",
    transition: "background 0.15s",
    whiteSpace: "nowrap" as const,
  },
  sendBtnDisabled: { background: "#e5e7eb", color: "#9ca3af", cursor: "not-allowed", boxShadow: "none" },
  progressBarWrap: {
    height: "3px", background: "#e5e7eb", borderRadius: "99px", marginTop: "2px", width: "80px",
  },
};

// ── Component ─────────────────────────────────────────────────────────────────

export function MilestoneCommentDrawer({
  open,
  onClose,
  milestoneName,
  milestoneId,
  fetchComments,
  saveComment,
  deleteComment,
  uploadFile,
}: MilestoneCommentDrawerProps) {
  const [comments, setComments] = useState<MilestoneComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [pendingFiles, setPendingFiles] = useState<
    { file: File; attachment: CommentAttachment | null; progress: number; uploading: boolean }[]
  >([]);
  const [sending, setSending] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const listRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);

  // ── Single source of truth: always load from API ──────────────────────────

  const loadComments = async (id: string) => {
    setLoading(true);
    try {
      const data = await fetchComments(id);
      setComments(data);
    } catch (error: any) {
      toast.error(error?.message || "Failed to load comments");
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch whenever the drawer opens or switches to a different milestone
  useEffect(() => {
    if (!open || !milestoneId) return;
    loadComments(milestoneId);
    setText("");
    setPendingFiles([]);
  }, [open, milestoneId]);

  // Scroll to bottom after list updates
  useEffect(() => {
    if (listRef.current && !loading) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [comments, loading]);

  // ── File upload ───────────────────────────────────────────────────────────

  const handleFilePick = async (files: FileList) => {
    if (!uploadFile) return;
    const picked = Array.from(files);
    setPendingFiles(prev => [
      ...prev,
      ...picked.map(f => ({ file: f, attachment: null, progress: 0, uploading: true })),
    ]);

    for (const file of picked) {
      try {
        const att = await uploadFile(file, (pct) => {
          setPendingFiles(prev =>
            prev.map(p => p.file === file ? { ...p, progress: pct } : p)
          );
        });
        setPendingFiles(prev =>
          prev.map(p => p.file === file ? { ...p, attachment: att, uploading: false, progress: 100 } : p)
        );
      } catch {
        toast.error(`${file.name} upload failed`);
        setPendingFiles(prev => prev.filter(p => p.file !== file));
      }
    }
  };

  const removePending = (file: File) =>
    setPendingFiles(prev => prev.filter(p => p.file !== file));

  // ── Send: save then re-fetch ──────────────────────────────────────────────

  const canSend =
    (text.trim().length > 0 || pendingFiles.some(p => p.attachment)) &&
    !sending &&
    !pendingFiles.some(p => p.uploading);

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const attachments = pendingFiles.filter(p => p.attachment).map(p => p.attachment!);
      await saveComment(milestoneId, text.trim(), attachments);
      // Re-fetch from API — single source of truth
      await loadComments(milestoneId);
      setText("");
      setPendingFiles([]);
    } catch (error: any) {
      toast.error(error?.message || "Failed to send comment");
    } finally {
      setSending(false);
    }
  };

  // ── Delete: delete then re-fetch ──────────────────────────────────────────

  const handleDelete = async (commentId: string) => {
    if (!deleteComment) return;
    setDeletingId(commentId);
    try {
      await deleteComment(milestoneId, commentId);
      // Re-fetch from API — single source of truth
      await loadComments(milestoneId);
    } catch (error: any) {
      toast.error(error?.message || "Failed to delete comment");
    } finally {
      setDeletingId(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleSend();
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {open && <div style={d.overlay} onClick={onClose} />}

      <div style={{ ...d.drawer, ...(open ? d.drawerVisible : d.drawerHidden) }}>

        {/* Header */}
        <div style={d.header}>
          <div style={d.headerLeft}>
            <div style={d.headerIcon}>
              <MessageSquare size={16} color="#F8B71F" />
            </div>
            <div>
              <div style={d.headerTitle}>Comments</div>
              <div style={d.headerSub}>
                <span style={{
                  display: "inline-block", maxWidth: "240px",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {milestoneName || "Milestone"}
                </span>
              </div>
            </div>
          </div>
          <button
            style={d.closeBtn}
            onClick={onClose}
            onMouseEnter={e => (e.currentTarget.style.background = "#e5e7eb")}
            onMouseLeave={e => (e.currentTarget.style.background = "#f3f4f6")}
          >
            <X size={15} />
          </button>
        </div>

        {/* Comment list */}
        <div style={d.commentList} ref={listRef}>

          {loading && (
            <div style={d.emptyState}>
              <div style={{ fontSize: "12px", color: "#9ca3af" }}>Loading comments…</div>
            </div>
          )}

          {!loading && comments.length === 0 && (
            <div style={d.emptyState}>
              <div style={d.emptyIcon}><MessageSquare size={20} color="#d1d5db" /></div>
              <div style={d.emptyText}>No comments yet</div>
              <div style={d.emptyHint}>Be the first to add a comment below.</div>
            </div>
          )}

          {!loading && comments.map(c => (
            <div key={c._id} style={d.commentCard}>

              {/* Per-card deleting overlay */}
              {deletingId === c._id && (
                <div style={d.deletingOverlay}>Deleting…</div>
              )}

              <div style={d.commentMeta}>
                <span style={d.commentTs}>
                  <Clock size={10} />
                  {formatTs(c.createdAt)}
                </span>
                {c.author?.name && (
                  <span style={d.commentAuthor}>{c.author.name}</span>
                )}
              </div>

              <div style={d.commentText}>{c.text}</div>

              {c.attachments?.length > 0 && (
                <div style={d.commentAttachments}>
                  {c.attachments.map((att, i) => (
                    <div key={i} style={d.commentFileRow}>
                      <FileText size={11} color="#2563eb" style={{ flexShrink: 0 }} />
                      <a
                        href={att.url}
                        target="_blank"
                        rel="noreferrer"
                        style={d.commentFileLink}
                        title={att.name || fileNameFromUrl(att.url)}
                      >
                        {att.name || fileNameFromUrl(att.url)}
                      </a>
                    </div>
                  ))}
                </div>
              )}

              {/* {deleteComment && (
                <button
                  style={d.commentDeleteBtn}
                  onClick={() => handleDelete(c._id)}
                  disabled={!!deletingId}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLButtonElement).style.color = "#ef4444";
                    (e.currentTarget as HTMLButtonElement).style.background = "#fee2e2";
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLButtonElement).style.color = "#d1d5db";
                    (e.currentTarget as HTMLButtonElement).style.background = "none";
                  }}
                  title="Delete comment"
                >
                  <Trash2 size={13} />
                </button>
              )} */}

            </div>
          ))}
        </div>

        {/* Compose */}
        <div style={d.compose}>
          <textarea
            ref={textRef}
            rows={3}
            style={d.textArea}
            placeholder="Write a comment… (Ctrl+Enter to send)"
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={e => {
              e.currentTarget.style.borderColor = "#F8B71F";
              e.currentTarget.style.boxShadow = "0 0 0 2px rgba(248,183,31,0.18)";
              e.currentTarget.style.background = "#fff";
            }}
            onBlur={e => {
              e.currentTarget.style.borderColor = "#e2e2e2";
              e.currentTarget.style.boxShadow = "none";
              e.currentTarget.style.background = "#fafafa";
            }}
          />

          {/* Pending file chips */}
          {pendingFiles.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "6px" }}>
              {pendingFiles.map((pf, i) => (
                <div key={i} style={d.pendingFile}>
                  <Paperclip size={10} />
                  <span style={{ maxWidth: "120px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {pf.file.name}
                  </span>
                  {pf.uploading ? (
                    <div style={d.progressBarWrap}>
                      <div style={{
                        ...d.progressBarWrap, background: "#F8B71F",
                        width: `${pf.progress}%`, height: "3px", margin: 0,
                      }} />
                    </div>
                  ) : (
                    <button
                      onClick={() => removePending(pf.file)}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: "0 2px", lineHeight: 1 }}
                    >
                      <X size={11} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          <div style={d.composeFooter}>
            <div style={d.attachRow}>
              {uploadFile && (
                <label style={d.attachBtn}>
                  <CloudUploadIcon sx={{ fontSize: 13 }} />
                  Attach
                  <input
                    type="file" hidden multiple
                    onChange={e => e.target.files && handleFilePick(e.target.files)}
                  />
                </label>
              )}
              <span style={{ fontSize: "10.5px", color: "#9ca3af" }}>Ctrl+Enter to send</span>
            </div>

            <button
              style={{ ...d.sendBtn, ...(!canSend ? d.sendBtnDisabled : {}) }}
              disabled={!canSend}
              onClick={handleSend}
            >
              <Send size={12} />
              {sending ? "Sending…" : "Send"}
            </button>
          </div>
        </div>

      </div>
    </>
  );
}

export default MilestoneCommentDrawer;